/* global window */
import { CartContext } from './context/CartContext';
import React, { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import '../styles/Cart.css';
import { Button } from 'react-bootstrap';
import { FaShoppingCart } from 'react-icons/fa';
import PropTypes from 'prop-types';
import { AiOutlineClose } from 'react-icons/ai';
import { BsCart2 } from 'react-icons/bs';
import { Link } from 'react-router-dom';
import AppContext from './context/AppContext';
import CartItem from './CartItem';
import getAvailableQty from '../helpers/getAvailableQty';

export default function Cart({ isForMobileNavigation = false, isForStickyMenu = false }) {
    const { appContext } = useContext(AppContext);
    const {
        showCartOverlay,
        setShowCartOverlay,
        cartState,
        checkoutData,
    } = useContext(CartContext);
    const appConfig = appContext.config.themeConfig;

    const subtotal = useMemo(() => cartState
        ?.map((item) => parseFloat(item.price * item.quantity))
        .reduce((prev, cur) => +prev + +cur, 0)
        ?? 0, [ cartState ]);

    /** Handlers */
    const hideCartOverlay = useCallback(
        () => setShowCartOverlay(false),
        [setShowCartOverlay],
    );

    const toggleShowCartOverlay = useCallback(
        () => setShowCartOverlay(!showCartOverlay),
        [setShowCartOverlay, showCartOverlay],
    );

    const cartButtonRef = useRef(null);
    const cartPopupRef = useRef(null);

    useEffect(() => {
        const detectOutsideClick = (e) => {
            if (cartButtonRef?.current?.contains(e.target)) {
                toggleShowCartOverlay();
            } else if (cartPopupRef?.current && !cartPopupRef.current.contains(e.target)) {
                setShowCartOverlay(false);
            }
        };
        window.addEventListener('click', detectOutsideClick, true);
        return () => {
            window.removeEventListener('click', detectOutsideClick, true);
        };
    }, [showCartOverlay]);

    return (
        <>
            <Button
                ref={cartButtonRef}
                className={'cart--button'}
                style={{
                    ...(isForMobileNavigation || isForStickyMenu) && ({ top: '0px' }),
                    ...(isForMobileNavigation || isForStickyMenu) && ({ background: 'transparent' }),
                }}
                size={'sm'}
                variant={'danger'}
                aria-label={'Cart'}
            >
                <FaShoppingCart
                    size={`${isForMobileNavigation ? '24px' : '16px'}`}
                    className={'me-1'} />
                {!isForMobileNavigation && <span
                    style={{
                        color: 'black',
                        ...isForStickyMenu && ({
                            background: 'var(--primary-color)',
                            padding: '8px 12px',
                            borderRadius: '3px',
                            marginLeft: '3px',
                        }),
                    }}>
                    {isForStickyMenu ? cartState?.length || 0 : <span>({cartState?.length || 0} ITEM)</span>}
                </span>}
            </Button>
            {showCartOverlay === true && (<>
                <div
                    className={`cart--background-overlay ${showCartOverlay ? 'visible' : ''}`}
                />
                <div
                    ref={cartPopupRef}
                    className={'cart-items-container'}
                    style={{ ...isForMobileNavigation && !isNaN(window.innerHeight) && ({ height: window.innerHeight + 'px' }) }}>

                    <div
                        className={'cart-items-container-inner'}>
                        <div
                            className={'cart-desktop-close'}><AiOutlineClose
                                color={'var(--action-color)'}
                                onClick={toggleShowCartOverlay}
                                style={{ margin: '15px' }}
                                size={'25px'} /></div>
                        {!checkoutData?.LineItems?.length && (<div
                            className={'d-flex flex-column align-items-center justify-content-between'}
                            style={{ width: '100%' }}>
                            <p
                                style={{
                                    padding: '15px',
                                    textAlign: 'center'
                                }}><b>Spend ${appConfig.freeShippingValue} and get free shipping!</b></p>
                            <div
                                className={'d-flex flex-column m-15-bottom m-15-top'}>
                                <BsCart2
                                    size={'150px'} />
                                <p
                                    style={{
                                        padding: '15px',
                                        textAlign: 'center'
                                    }}><b>Your cart is empty.</b></p>
                            </div>
                            <Link
                                onClick={toggleShowCartOverlay}
                                to={'/'}
                                style={{
                                    textDecoration: 'none',
                                    width: '100%'
                                }}>
                                <Button
                                    style={{
                                        backgroundColor: 'var(--header-accent-color)',
                                        color: 'white',
                                        width: '100%'
                                    }}>Shop our products</Button>
                            </Link>
                        </div>)}
                        {checkoutData?.LineItems?.length > 0 && <>
                            {checkoutData.totalPrice &&
                            <>
                                {parseFloat(checkoutData.totalPrice) > 0 && parseFloat(checkoutData.totalPrice) < appConfig.freeShippingValue && <p
                                    style={{
                                        padding: '15px',
                                        textAlign: 'center'
                                    }}><b>Spend ${(appConfig.freeShippingValue - parseFloat(checkoutData.totalPrice)).toFixed(2)} more and get free shipping!</b></p>}
                                {parseFloat(checkoutData.totalPrice) > 0 && parseFloat(checkoutData.totalPrice) >= appConfig.freeShippingValue && <p
                                    style={{
                                        padding: '15px',
                                        textAlign: 'center'
                                    }}><b>You&apos;re eligible for free shipping!</b></p>}
                            </>}
                            <table
                                className={'cart--contents'}>
                                <thead>
                                    <tr>
                                        <th
                                            style={{
                                                textAlign: 'left',
                                                paddingLeft: '20px',
                                            }}
                                            colSpan={2}>
                                        Cart Items
                                        </th>
                                        <th>Qty</th>
                                        <th>Item Price</th>
                                        <th>Item Total</th>
                                        <th>&nbsp;</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {checkoutData?.LineItems?.map((lineItem) => <CartItem
                                        key={lineItem.id}
                                        lineItemId={lineItem.id}
                                        isMobile={isForMobileNavigation}
                                        inventoryLevelId={lineItem.inventoryLevelId}
                                        price={lineItem.totalUnitPrice}
                                        quantity={lineItem.currentQuantity}
                                        sku={lineItem.sku}
                                        title={lineItem.title}
                                        availableQuantity={getAvailableQty(lineItem)}
                                        availableForSale={lineItem.availableForSale}
                                        imageUrl={lineItem.imageUrl}
                                        handle={lineItem.productHandle}
                                        bypassNegativeQuantity={lineItem.bypassNegativeQuantity}
                                    />)}
                                </tbody>
                            </table>
                        </>}
                    </div>
                    {checkoutData?.LineItems?.length > 0 && <div
                        className={`d-flex flex-column m-10 cart-checkout-column ${isForMobileNavigation ? 'cart--summary-mobile' : ''}`}>
                        <p
                            className={'mt-1'}
                            style={{ textAlign: 'center' }}>
                            <span><b>Total Items: </b></span>
                            <span>
                                <b>{cartState?.reduce((accumulator, item) => accumulator + item.quantity, 0) ?? 0}</b>
                            </span>
                        </p>
                        <p
                            style={{ textAlign: 'center' }}>
                            <span><b>Subtotal: </b></span>
                            <span>
                                <b>${subtotal.toFixed(2)}</b>
                            </span>
                        </p>
                        <p
                            style={{ textAlign: 'center' }}>
                            <span><b>Discount: </b></span>
                            <span>
                                <b>${(subtotal - (checkoutData?.subtotalPrice ?? 0)).toFixed(2)}</b>
                            </span>
                        </p>
                        <p
                            style={{ textAlign: 'center' }}>
                            <span><b>Total Amount: </b></span>
                            <span>
                                <b>${(checkoutData?.totalPrice ?? 0).toFixed(2)}</b>
                            </span>
                        </p>
                        <div
                            className={'d-flex flex-row gap-3'}
                            style={{
                                width: '100%',
                                alignSelf: 'flex-start',
                                justifyContent: 'end',
                            }}>
                            <Link
                                onClick={hideCartOverlay}
                                style={{
                                    width: '25%',
                                    textDecoration: 'none',
                                }}
                                to={'/cart.php'}>
                                <Button
                                    aria-label={'Go to cart page'}
                                    className={'cart-viewcart-button m-15-bottom m-5-top'}>
                                View cart
                                </Button>
                            </Link>
                            <a
                                style={{
                                    width: '25%',
                                    textDecoration: 'none'
                                }}
                                href={checkoutData.webUrl}><Button
                                    aria-label={'Proceed to checkout'}
                                    className={'cart-checkout-button m-15-bottom m-5-top'}>Checkout</Button></a>
                        </div>
                    </div>}
                </div>
            </> )}
        </>
    );
}

Cart.propTypes = {
    isForMobileNavigation: PropTypes.bool,
    isForStickyMenu: PropTypes.bool,
};